import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { PageHeader } from '@/components/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import {
  confirmBusinessUser,
  fetchBusinessUsersPool,
  selectBusinessUsersPoolUpdateError,
  selectBusinessUsersPoolUpdating,
  selectBusinessUsersPoolError,
  selectBusinessUsersPoolHasFetched,
  selectBusinessUsersPoolHasMore,
  selectBusinessUsersPoolLoading,
  selectBusinessUsersPoolNextToken,
  selectBusinessUsersPoolUsers,
  updateBusinessUser,
  updateBusinessUserEnabled,
  type AuthUserTypeName,
  type BusinessUserSummary,
  type UserAuthRole,
} from '@/features/business-users-pool';
import { fetchUsers, selectHasListedUsers, selectListUsersLoading, selectUserByCognitoSubject } from '@/features/users';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { IBusinessUser } from '@/domain/entities/user';

const PAGE_LIMIT = 25;
const ROLE_OPTIONS: UserAuthRole[] = ['owner', 'viewer', 'super_admin'];
const USER_TYPE_OPTIONS: AuthUserTypeName[] = ['business_user', 'system_user'];

function normalize(value: string) {
  return value.trim().toLowerCase();
}

function getUserKey(user: BusinessUserSummary) {
  return user.username?.trim() || user.email?.trim() || '';
}

function formatDate(value?: number) {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) return 'Not available';

  const date = new Date(value * 1000);
  if (Number.isNaN(date.getTime())) {
    return 'Not available';
  }

  return date.toLocaleString();
}

function getBusinessProfileLabel(user: IBusinessUser | null) {
  if (!user) return '';

  const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return fullName || user.email || user.applicationUserId;
}

function renderStatusBadge(user: BusinessUserSummary) {
  if (user.enabled === false) {
    return <Badge variant="destructive">Disabled</Badge>;
  }

  if (user.accountStatus) {
    return <Badge variant="secondary">{user.accountStatus}</Badge>;
  }

  if (user.userStatus) {
    return <Badge variant="outline">{user.userStatus}</Badge>;
  }

  return <Badge variant="outline">Unknown status</Badge>;
}

export default function BusinessUsersPoolPage() {
  const dispatch = useAppDispatch();
  const confirm = useConfirmDialog();

  const users = useAppSelector(selectBusinessUsersPoolUsers);
  const isLoading = useAppSelector(selectBusinessUsersPoolLoading);
  const error = useAppSelector(selectBusinessUsersPoolError);
  const hasFetched = useAppSelector(selectBusinessUsersPoolHasFetched);
  const hasMore = useAppSelector(selectBusinessUsersPoolHasMore);
  const nextToken = useAppSelector(selectBusinessUsersPoolNextToken);
  const isUpdating = useAppSelector(selectBusinessUsersPoolUpdating);
  const updateError = useAppSelector(selectBusinessUsersPoolUpdateError);

  const [query, setQuery] = useState('');
  const [selectedKey, setSelectedKey] = useState('');
  const [roleDraft, setRoleDraft] = useState<UserAuthRole | ''>('');
  const [userTypeDraft, setUserTypeDraft] = useState<AuthUserTypeName | ''>('');

  useEffect(() => {
    if (hasFetched) return;

    dispatch(fetchBusinessUsersPool({ limit: PAGE_LIMIT }))
      .unwrap()
      .catch((err) => {
        toast.error(err?.message || 'Failed to load business users pool');
      });
  }, [dispatch, hasFetched]);

  useEffect(() => {
    if (selectedKey) return;
    if (users.length === 0) return;

    const firstKey = getUserKey(users[0]);
    if (firstKey) {
      setSelectedKey(firstKey);
    }
  }, [users, selectedKey]);

  useEffect(() => {
    if (!selectedKey || users.length === 0) return;

    const stillExists = users.some((user) => getUserKey(user) === selectedKey);
    if (stillExists) return;

    const firstKey = getUserKey(users[0]);
    if (firstKey) {
      setSelectedKey(firstKey);
    }
  }, [users, selectedKey]);

  const filteredUsers = useMemo(() => {
    const normalizedQuery = normalize(query);
    if (!normalizedQuery) {
      return users;
    }

    return users.filter((user) => {
      const haystack = [
        user.username,
        user.email,
        user.fullName,
        user.role,
        user.userType,
        user.accountStatus,
        user.userStatus,
      ]
        .filter(Boolean)
        .join(' ');

      return normalize(haystack).includes(normalizedQuery);
    });
  }, [query, users]);

  const selectedUser = useMemo(() => {
    return users.find((user) => getUserKey(user) === selectedKey) ?? null;
  }, [selectedKey, users]);
  const selectedCognitoSubject = selectedUser?.sub?.trim() || '';
  const selectedBusinessProfile = useAppSelector(selectUserByCognitoSubject(selectedCognitoSubject));
  const isListingBusinessProfiles = useAppSelector(selectListUsersLoading);
  const hasListedBusinessProfiles = useAppSelector(selectHasListedUsers);

  useEffect(() => {
    if (!selectedUser) {
      setRoleDraft('');
      setUserTypeDraft('');
      return;
    }

    setRoleDraft(selectedUser.role ?? '');
    setUserTypeDraft(selectedUser.userType ?? '');
  }, [selectedUser]);

  useEffect(() => {
    if (!selectedCognitoSubject) return;
    if (selectedBusinessProfile) return;
    if (isListingBusinessProfiles || hasListedBusinessProfiles) return;

    void dispatch(fetchUsers({ limit: 100 }));
  }, [dispatch, hasListedBusinessProfiles, isListingBusinessProfiles, selectedBusinessProfile, selectedCognitoSubject]);

  const hasPendingAccessChanges = Boolean(
    selectedUser && (roleDraft !== (selectedUser.role ?? '') || userTypeDraft !== (selectedUser.userType ?? '')),
  );
  const selectedBusinessProfileLabel = getBusinessProfileLabel(selectedBusinessProfile);

  const loadMore = async () => {
    if (isLoading || !hasMore) return;

    try {
      await dispatch(fetchBusinessUsersPool({ limit: PAGE_LIMIT, nextToken: nextToken ?? undefined })).unwrap();
    } catch (err) {
      const errorPayload = err as { message?: string };
      toast.error(errorPayload?.message || 'Failed to load more business users');
    }
  };

  const refresh = async () => {
    if (isLoading) return;

    try {
      await dispatch(fetchBusinessUsersPool({ limit: PAGE_LIMIT })).unwrap();
      toast.success('Business users pool refreshed');
    } catch (err) {
      const errorPayload = err as { message?: string };
      toast.error(errorPayload?.message || 'Failed to refresh business users pool');
    }
  };

  const saveAccessAttributes = async () => {
    if (!selectedUser?.username) return;
    if (!roleDraft || !userTypeDraft) return;
    if (!hasPendingAccessChanges) return;

    const ok = await confirm({
      title: 'Save access attribute changes?',
      description: 'This updates the Cognito auth role and user type for the selected user.',
      confirmLabel: 'Save Changes',
    });
    if (!ok) return;

    try {
      await dispatch(
        updateBusinessUser({
          username: selectedUser.username,
          attributes: {
            role: roleDraft,
            userType: userTypeDraft,
          },
        }),
      ).unwrap();
      toast.success('Business user updated');
    } catch (err) {
      const errorPayload = err as { message?: string };
      toast.error(errorPayload?.message || 'Failed to update business user');
    }
  };

  const toggleEnabledState = async () => {
    if (!selectedUser?.username || selectedUser.enabled === undefined) return;

    const nextEnabled = !selectedUser.enabled;
    const ok = await confirm({
      title: nextEnabled ? 'Enable this user account?' : 'Disable this user account?',
      description: nextEnabled
        ? 'This will restore Cognito sign-in access for this user.'
        : 'This will block Cognito sign-in access for this user until re-enabled.',
      confirmLabel: nextEnabled ? 'Enable' : 'Disable',
      variant: nextEnabled ? 'default' : 'destructive',
    });
    if (!ok) return;

    try {
      await dispatch(
        updateBusinessUserEnabled({
          username: selectedUser.username,
          enabled: nextEnabled,
        }),
      ).unwrap();
      toast.success(`Business user ${nextEnabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      const errorPayload = err as { message?: string };
      toast.error(errorPayload?.message || 'Failed to update business user enabled state');
    }
  };

  const confirmSelectedUser = async () => {
    if (!selectedUser?.username) return;

    const ok = await confirm({
      title: 'Confirm this user account?',
      description: 'This will run Cognito admin confirm signup and usually sets the user status to CONFIRMED.',
      confirmLabel: 'Confirm User',
    });
    if (!ok) return;

    try {
      await dispatch(
        confirmBusinessUser({
          username: selectedUser.username,
        }),
      ).unwrap();
      toast.success('Business user confirmed');
    } catch (err) {
      const errorPayload = err as { message?: string };
      toast.error(errorPayload?.message || 'Failed to confirm business user');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Business Users Pool"
        description="Browse users from the business Cognito user pool through the auth backend."
        right={
          <Button type="button" variant="outline" onClick={refresh} disabled={isLoading}>
            {isLoading ? 'Loading…' : 'Refresh'}
          </Button>
        }
      />

      {error ? <p className="text-destructive text-sm">{error}</p> : null}

      <div className="grid gap-6 lg:grid-cols-[420px_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>Pool Users</CardTitle>
            <CardDescription>Search by username, email, full name, role, or status.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search business user pool…"
            />

            <div className="space-y-2">
              {filteredUsers.length === 0 ? (
                <p className="text-muted-foreground text-sm">{isLoading ? 'Loading…' : 'No users found.'}</p>
              ) : (
                filteredUsers.map((user, index) => {
                  const userKey = getUserKey(user) || `${user.email ?? 'user'}-${index}`;
                  const isActive = userKey === selectedKey;
                  const title = user.fullName?.trim() || user.email || user.username || 'Unnamed user';

                  return (
                    <button
                      key={userKey}
                      type="button"
                      onClick={() => setSelectedKey(userKey)}
                      className={[
                        'w-full rounded-md border p-3 text-left transition',
                        isActive ? 'border-primary bg-primary/5' : 'hover:bg-muted/40',
                      ].join(' ')}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{title}</p>
                          <p className="text-muted-foreground truncate text-xs">{user.email || user.username}</p>
                        </div>

                        <div className="flex flex-wrap items-center justify-end gap-2">
                          {user.role ? <Badge>{user.role}</Badge> : null}
                          {renderStatusBadge(user)}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={loadMore}
              disabled={isLoading || !hasMore || filteredUsers.length !== users.length}
            >
              {filteredUsers.length !== users.length
                ? 'Search active (load more disabled)'
                : isLoading
                  ? 'Loading…'
                  : hasMore
                    ? 'Load more'
                    : 'No more users'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>User Details</CardTitle>
            <CardDescription>Selected Cognito user metadata from the business pool.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {selectedUser ? (
              <>
                <div className="space-y-4 rounded-lg border p-4">
                  <div>
                    <h3 className="text-sm font-semibold">Access Attributes</h3>
                    <p className="text-muted-foreground mt-1 text-sm">
                      Only auth `role` and `userType` are editable here.
                    </p>
                  </div>

                  {updateError ? <p className="text-destructive text-sm">{updateError}</p> : null}

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">Role</p>
                      <Select
                        value={roleDraft || undefined}
                        onValueChange={(value) => setRoleDraft(value as UserAuthRole)}
                        disabled={!selectedUser.username || isUpdating}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLE_OPTIONS.map((role) => (
                            <SelectItem key={role} value={role}>
                              {role}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">User Type</p>
                      <Select
                        value={userTypeDraft || undefined}
                        onValueChange={(value) => setUserTypeDraft(value as AuthUserTypeName)}
                        disabled={!selectedUser.username || isUpdating}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select user type" />
                        </SelectTrigger>
                        <SelectContent>
                          {USER_TYPE_OPTIONS.map((userType) => (
                            <SelectItem key={userType} value={userType}>
                              {userType}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      type="button"
                      onClick={saveAccessAttributes}
                      disabled={
                        !selectedUser.username || !roleDraft || !userTypeDraft || !hasPendingAccessChanges || isUpdating
                      }
                    >
                      {isUpdating ? 'Saving…' : 'Save Access Attributes'}
                    </Button>
                  </div>
                </div>

                <div className="space-y-4 rounded-lg border p-4">
                  <div>
                    <h3 className="text-sm font-semibold">Cognito Controls</h3>
                    <p className="text-muted-foreground mt-1 text-sm">
                      `enabled` controls account access. `userStatus` reflects confirmation state.
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <Button
                      type="button"
                      variant={selectedUser.enabled === false ? 'default' : 'outline'}
                      onClick={toggleEnabledState}
                      disabled={!selectedUser.username || selectedUser.enabled === undefined || isUpdating}
                    >
                      {isUpdating ? 'Saving…' : selectedUser.enabled === false ? 'Enable Account' : 'Disable Account'}
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={confirmSelectedUser}
                      disabled={!selectedUser.username || selectedUser.userStatus === 'CONFIRMED' || isUpdating}
                    >
                      {isUpdating
                        ? 'Saving…'
                        : selectedUser.userStatus === 'CONFIRMED'
                          ? 'Already Confirmed'
                          : 'Confirm User'}
                    </Button>
                  </div>
                </div>

                <dl className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <dt className="text-muted-foreground text-xs font-medium tracking-wide uppercase">Full Name</dt>
                    <dd className="mt-1 text-sm">{selectedUser.fullName || 'Not available'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground text-xs font-medium tracking-wide uppercase">Email</dt>
                    <dd className="mt-1 text-sm">{selectedUser.email || 'Not available'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground text-xs font-medium tracking-wide uppercase">Username</dt>
                    <dd className="mt-1 text-sm">
                      {selectedCognitoSubject ? (
                        isListingBusinessProfiles ? (
                          <span className="inline-flex items-center gap-2">
                            <LoadingSpinner size={20} />
                            <span className="text-muted-foreground">Loading profile…</span>
                          </span>
                        ) : selectedBusinessProfile ? (
                          <Link
                            to={`/admin/users?userId=${encodeURIComponent(selectedBusinessProfile.applicationUserId)}`}
                            className="text-primary hover:underline"
                          >
                            {selectedBusinessProfileLabel}
                          </Link>
                        ) : hasListedBusinessProfiles ? (
                          'No Profile'
                        ) : (
                          <span className="text-muted-foreground">Profile not requested</span>
                        )
                      ) : (
                        'No Profile'
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground text-xs font-medium tracking-wide uppercase">Sub</dt>
                    <dd className="mt-1 text-sm">{selectedUser.sub || 'Not available'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground text-xs font-medium tracking-wide uppercase">Enabled</dt>
                    <dd className="mt-1 text-sm">
                      {selectedUser.enabled === undefined ? 'Not available' : String(selectedUser.enabled)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                      Account Status
                    </dt>
                    <dd className="mt-1 text-sm">{selectedUser.accountStatus || 'Not available'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground text-xs font-medium tracking-wide uppercase">User Status</dt>
                    <dd className="mt-1 text-sm">{selectedUser.userStatus || 'Not available'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground text-xs font-medium tracking-wide uppercase">Created At</dt>
                    <dd className="mt-1 text-sm">{formatDate(selectedUser.createdAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground text-xs font-medium tracking-wide uppercase">Updated At</dt>
                    <dd className="mt-1 text-sm">{formatDate(selectedUser.updatedAt)}</dd>
                  </div>
                </dl>
              </>
            ) : (
              <p className="text-muted-foreground text-sm">
                {isLoading ? 'Loading business users…' : 'Select a user to inspect Cognito pool details.'}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
